import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { contactInfo } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Vahvista, että saamme olla sinuun yhteydessä.");
    if (!form.phone && !form.email) return toast.error("Anna puhelinnumero tai sähköposti.");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Kiitos! Olemme sinuun yhteydessä pian.");
      setForm({ name: "", phone: "", email: "", message: "", consent: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lähetys epäonnistui. Yritä uudelleen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="yhteys" className="py-16 sm:py-24 bg-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <span className="text-base font-bold text-[#245037] uppercase tracking-wide">
            Ota yhteyttä
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#1C1917]">
            Jätä yhteystietosi, niin autan sinua
          </h2>
          <p className="mt-4 text-lg sm:text-xl leading-relaxed text-[#44403C]">
            Kerro omin sanoin, missä kaipaat apua – virheistä, onnistumisista tai
            mieltä askarruttavista pulmista. Sana on vapaa. Otan sinuun yhteyttä
            mahdollisimman pian.
          </p>

          <div className="mt-8 space-y-5">
            <a href={contactInfo.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-2xl bg-[#245037] flex items-center justify-center shrink-0">
                <Phone strokeWidth={2.5} className="w-6 h-6 text-[#E8C88A]" />
              </div>
              <div>
                <p className="text-base text-[#78716C]">Soita</p>
                <p className="text-xl font-bold text-[#1C1917] group-hover:text-[#245037] transition-colors">
                  {contactInfo.phone}
                </p>
              </div>
            </a>
            <a href={`mailto:${contactInfo.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-2xl bg-[#245037] flex items-center justify-center shrink-0">
                <Mail strokeWidth={2.5} className="w-6 h-6 text-[#E8C88A]" />
              </div>
              <div>
                <p className="text-base text-[#78716C]">Sähköposti</p>
                <p className="text-xl font-bold text-[#1C1917] group-hover:text-[#245037] transition-colors">
                  {contactInfo.email}
                </p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#245037] flex items-center justify-center shrink-0">
                <MapPin strokeWidth={2.5} className="w-6 h-6 text-[#E8C88A]" />
              </div>
              <div>
                <p className="text-base text-[#78716C]">Palvelualue</p>
                <p className="text-lg font-bold text-[#1C1917]">{contactInfo.area}</p>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-white rounded-2xl p-8 shadow-sm border border-[#E8E2D5]"
        >
          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-lg font-semibold text-[#1C1917] mb-2">
                Nimi
              </label>
              <input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={update("name")}
                required
                className="w-full rounded-xl border-2 border-[#D6D3D1] bg-white px-4 py-3 text-lg text-[#1C1917] focus:border-[#245037] focus:ring-2 focus:ring-[#245037]/20 outline-none"
                placeholder="Etunimesi"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className="block text-lg font-semibold text-[#1C1917] mb-2">
                  Puhelin
                </label>
                <input
                  id="phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  className="w-full rounded-xl border-2 border-[#D6D3D1] bg-white px-4 py-3 text-lg text-[#1C1917] focus:border-[#245037] focus:ring-2 focus:ring-[#245037]/20 outline-none"
                  placeholder="046 802 8874"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-lg font-semibold text-[#1C1917] mb-2">
                  Sähköposti
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full rounded-xl border-2 border-[#D6D3D1] bg-white px-4 py-3 text-lg text-[#1C1917] focus:border-[#245037] focus:ring-2 focus:ring-[#245037]/20 outline-none"
                  placeholder="nimi@esimerkki.fi"
                />
              </div>
            </div>
            <div>
              <label htmlFor="message" className="block text-lg font-semibold text-[#1C1917] mb-2">
                Viesti
              </label>
              <textarea
                id="message"
                data-testid="input-message"
                value={form.message}
                onChange={update("message")}
                required
                rows={4}
                className="w-full rounded-xl border-2 border-[#D6D3D1] bg-white px-4 py-3 text-lg text-[#1C1917] focus:border-[#245037] focus:ring-2 focus:ring-[#245037]/20 outline-none resize-none"
                placeholder="Kerro, missä kaipaat apua..."
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="input-consent"
                checked={form.consent}
                onChange={update("consent")}
                className="mt-1 w-5 h-5 accent-[#245037] shrink-0"
              />
              <span className="text-base text-[#44403C]">
                Hyväksyn, että yllä olevia tietoja käytetään minuun yhteydenottamiseen.
              </span>
            </label>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#245037] text-white hover:bg-[#1A3B28] rounded-xl px-8 py-4 text-lg font-bold transition-transform active:scale-95 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Lähetetään...
                </>
              ) : (
                <>
                  Lähetä viesti <Send strokeWidth={2.5} className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
